import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Input, Loader } from 'ncoded-component-library';
import CheckToggleField from 'components/CheckToggleField';
import CheckToggle from 'components/CheckToggle';
import TextAreaField from 'components/TextAreaField';
import { getHasElementsInArray } from 'utils';
import api from 'api';
import useInfinitePagination from 'hooks/useInfinitePagination';
import useDebounceSearch from 'hooks/useDebounceSearch';
import { Contact } from 'models/Contact';
import { DEFAULT_LIMIT } from 'data/global';
import SearchIcon from 'icons/Search.icon';
import SelectRowsPlaceholder from 'components/SelectRowsPlaceholder';
import SelectContacts from '../SelectContacts';

import './InviteContactsForm.styles.scss';

export const SORT_VALUES = {
  NAME: 'sortName',
};

// Width of the "Resolve account" column in SelectContacts.
const TRAILING_COLUMN_WIDTHS = [140];

export type InviteContactsFormValues = {
  contacts: Record<string, boolean>;
  includeMessage?: boolean;
  customMessage?: string;
};

type InviteContactsFormProps = {
  className?: string;
  dealRoomId: string;
  submitText: string;
  onSubmit: (values: InviteContactsFormValues) => void;
  onClose: () => void;
  updateIsEmpty: (value: boolean) => void;
};

const InviteContactsForm: React.FC<InviteContactsFormProps> = (props) => {
  const {
    className,
    dealRoomId,
    submitText,
    onSubmit,
    onClose,
    updateIsEmpty,
  } = props;

  const [showContactsWithAccount, setShowContactsWithAccount] = useState(false);
  const [sort, setSort] = useState(SORT_VALUES.NAME);

  const initialFetch = useRef(true);

  const { t } = useTranslation();

  const classes = classNames('invite-contacts-form', className);

  const { searchString, searchInputValue, onSearchInputChange } =
    useDebounceSearch();

  const { loading, items, totalItems, onContainerScrolled } =
    useInfinitePagination<Contact>({
      condition: !!dealRoomId,
      resetItemsWhenReset: false,
      resetDeps: [searchString, showContactsWithAccount, sort],
      dependencies: [searchString, showContactsWithAccount, sort],
      makeRequest: async (currentPage) =>
        api.contacts
          .getContacts({
            $page: currentPage.toString(),
            $limit: DEFAULT_LIMIT,
            $searchString: searchString,
            $sort: sort,
            ...(showContactsWithAccount && { showContactsWithAccount }),
          })
          .then(({ data }) => data),
    });

  const itemsHasElements = getHasElementsInArray(items);

  const isInitialLoading = loading && initialFetch.current;

  useEffect(() => {
    if (!initialFetch.current || loading) return;

    initialFetch.current = false;
    updateIsEmpty(!itemsHasElements);
  }, [loading, itemsHasElements, updateIsEmpty]);

  return (
    <Form
      onSubmit={onSubmit}
      render={({
        submitting,
        invalid,
        values: { includeMessage },
        handleSubmit,
      }) => (
        <form onSubmit={handleSubmit} className={classes}>
          {/* The initial fetch is covered by the contacts placeholder, so the
              modal keeps its size instead of growing once contacts arrive. */}
          {((loading && !isInitialLoading) || submitting) && <Loader />}

          <div className="invite-contacts-form__filter">
            <p>{t('filterContactsWithAccount')}</p>
            <CheckToggle
              checked={showContactsWithAccount}
              onChange={(e) => setShowContactsWithAccount(e.target.checked)}
            />
          </div>

          <div className="invite-contacts-form__filter">
            <p>{t('filterContactsByName')}</p>
            <Input
              prefixNode={<SearchIcon />}
              placeholder={t('search')}
              value={searchInputValue}
              onChange={({ target: { value } }) => onSearchInputChange(value)}
            />
          </div>

          {isInitialLoading ? (
            <SelectRowsPlaceholder
              hasCount
              trailingWidths={TRAILING_COLUMN_WIDTHS}
            />
          ) : itemsHasElements ? (
            <>
              <p className="displayed-contacts">
                {t('displayedContacts', { count: totalItems })}
              </p>

              <SelectContacts
                contacts={items}
                sort={sort}
                updateSort={setSort}
                onContainerScroll={onContainerScrolled}
              />
            </>
          ) : (
            <p className="empty-contacts">{t('noContactsSatisfyCriteria')}</p>
          )}

          {(isInitialLoading || itemsHasElements) && (
            <>
              <Field
                name="includeMessage"
                type="checkbox"
                label={t('includeMessage')}
                component={CheckToggleField}
              />
              {includeMessage && (
                <Field
                  name="customMessage"
                  component={TextAreaField}
                  rows={3}
                />
              )}
            </>
          )}

          <div className="actions">
            <Button variant="link" onClick={onClose}>
              {t('cancel')}
            </Button>

            <Button
              type="submit"
              disabled={submitting || invalid || isInitialLoading}
            >
              {submitText}
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default InviteContactsForm;
